import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectFilteredEdges } from '../map/slice/mapSelectors';
import { edgeToGeoJson } from '../utils/geoJsonUtils';
import {
  selectPlotterCapacities,
  selectShowFlowArrows,
} from '../plotter/plotterSelectors';
import { Edge } from '@/types';
import { FlowSource } from '@/types/api/Flow';
import { useMapLayer } from '../hooks/useMapLayer';

const getEdgeStyle = () => [
  {
    filter: ['<=', ['get', 'head'], 0],
    style: {
      'stroke-width': 4,
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
    },
  },
  {
    filter: ['>', ['get', 'head'], 0],
    style: {
      'shape-points': 3,
      'shape-radius': 10,
      'shape-fill-color': ['get', 'color'],
      'shape-stroke-color': ['get', 'color'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
];

const extractGeoJsonProps = (
  data: { edgeId: number; capacityId: number } & FlowSource & Edge,
  _arrowHead: boolean,
) => {
  let color = 'blue';
  if (data.utilization > 0.8) {
    color = 'red';
  } else if (data.utilization > 0.5) {
    color = 'orange';
  } else if (data.utilization == 0) {
    color = 'grey';
  }

  return {
    key: `edge-${data.edgeId}`,
    id: data.edgeId,
    color: color,
  };
};

export const MapFlows = ({ zIndex = 1 }) => {
  const edges = useSelector(selectFilteredEdges);
  const showFlowArrows = useSelector(selectShowFlowArrows);

  const capacities = useSelector(selectPlotterCapacities);

  const _features = useMemo(
    () =>
      capacities
        .filter((capacity) => capacity.capacity_type === 'SEGMENT')
        .flatMap((capacity) => {
          const record = capacity.best_available;
          if (!record) return [];
          const edgeIds =
            capacity.flow_direction === 'BACKHAUL'
              ? [...record.against_edges]
              : [...record.with_edges];

          return edgeIds
            .map((edgeId) => ({
              edge: edges[edgeId],
              capacity: record,
              direction: capacity.flow_direction,
            }))
            .filter(({ edge }) => edge);
        })
        .flatMap(({ edge, capacity, direction }) => {
          const positions =
            direction === 'FORWARDHAUL'
              ? edge.positions
              : edge.positions.slice().reverse();
          return edgeToGeoJson(
            {
              ...edge,
              ...capacity,
              positions,
              edgeId: Number(edge.id),
              capacityId: Number(capacity.id),
            },
            extractGeoJsonProps,
            true,
          );
        }),
    [capacities, edges],
  );

  const features = useMemo(() => {
    return showFlowArrows ? _features : [];
  }, [_features, showFlowArrows]);

  const style = useMemo(() => getEdgeStyle(), []);

  useMapLayer(features, { zIndex, style });

  return <></>;
};
